<template>
  <div>
    <!-- CHURN AUDIT -->
    <div v-if="stage === 'connect'">
      <div class="border-brand-sand border-2 py-8 px-12 max-w-screen-sm mx-auto shadow-sm rounded-lg">
        <p class="font-bold text-center text-brand-black text-2xl">Connect Your Stripe Account</p>
        <p class="font-semibold text-brand-brown text-center text-lg mt-1">With one click, we'll be able to start your churn audit.</p>
        <div>
          <div class="mt-8 text-center">
            <font-awesome-icon :icon="['fab', 'stripe']" class="text-6xl" :style="{ color: '#32325d' }" />
          </div>
        </div>
        <div class="mt-4">
          <a :href="stripeConnectUrlLive" class="el-button w-full text-lg font-semibold py-4 el-button--primary"> Connect Your Stripe Account → </a>
        </div>
      </div>
      <!-- Alternative to Stripe -->
      <div class="border-brand-sand border-2 py-8 px-12 my-8 max-w-screen-sm mx-auto shadow-sm rounded-lg">
        <p class="font-bold text-center text-brand-black text-2xl">Using another billing provider?</p>
        <p class="font-semibold text-brand-brown text-center text-lg mt-1">Our integrations team can help!</p>
        <div>
          <div class="mt-8 text-center">
            <font-awesome-icon :icon="['fas', 'file-invoice-dollar']" class="text-6xl" :style="{ color: '#32325d' }" />
          </div>
        </div>
        <div class="text-center mt-4">
          <a @click="scheduleCall" class="el-button w-full text-lg font-semibold py-4 el-button--primary"> Talk to our Team → </a>
        </div>
      </div>
    </div>

    <div v-else-if="stage === 'processing'">
      <div class="container max-w-screen-md">
        <wavy-card>
          <div class="-mb-36">
            <div class="absolute left-0 right-0 top-0 flex justify-center">
              <div class="-mt-12 text-6xl">
                <img src="@/assets/brand/icon.svg" class="w-auto inline-block h-16 p-2.5 bg-brand-black rounded-full" alt="Logo" />
              </div>
            </div>
            <div class="mt-8 text-center">
              <p class="font-bold text-center text-brand-black text-2xl">
                Churnkey is building something great for you. You'll get an email when we're ready.
              </p>
              <p class="font-semibold text-brand-brown text-center text-lg mt-1">
                Importing your Stripe transactions, subscriptions, and coupons can take up to several hours. In the meantime, let's get you started on
                installing Churnkey's code snippet.
              </p>
              <div class="mt-12">
                <el-button @click="goToBuilder" type="primary" class="text-lg font-semibold py-4">Install Code Snippet →</el-button>
              </div>
            </div>
          </div>
        </wavy-card>
      </div>
    </div>

    <div v-else-if="stage === 'error'">
      <div class="container max-w-screen-md">
        <wavy-card>
          <div class="-mb-36">
            <div class="absolute left-0 right-0 top-0 flex justify-center">
              <font-awesome-layers class="-mt-8 text-6xl">
                <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-red" />
                <font-awesome-icon :icon="['fab', 'stripe']" class="text-black opacity-50 text-3xl" />
              </font-awesome-layers>
            </div>
            <div class="mt-8 text-center">
              <p class="font-bold text-center text-brand-black text-2xl">Sorry. Stripe returned an error.</p>
              <p class="font-semibold text-brand-brown text-center text-lg mt-1">
                Churnkey couldn't access your data, or there was no data to access. Please try connecting your Stripe account one more time.
              </p>
              <div class="mt-12">
                <el-button type="primary" class="text-lg font-semibold py-4">Try to Connect Stripe Again →</el-button>
              </div>
            </div>
          </div>
        </wavy-card>
        <div class="mt-64">
          <div class="text-center text-gray-600 font-semibold">We're here to help.</div>
          <div class="text-center mt-3">
            <el-button type="primary" plain>Chat Help Now</el-button>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="stage === 'ready'">
      <div class="container max-w-screen-md">
        <wavy-card>
          <div class="-mb-36">
            <div class="absolute left-0 right-0 top-0 flex justify-center">
              <div class="-mt-12 text-6xl">
                <img src="@/assets/brand/icon.svg" class="w-auto inline-block h-16 p-2.5 bg-brand-black rounded-full" alt="Logo" />
              </div>
            </div>
            <div class="mt-8 text-center">
              <p class="font-bold text-center text-brand-black text-2xl">Churnkey is connected to your Stripe data.</p>
              <p class="font-semibold text-brand-brown text-center text-lg mt-1">
                We'll get started on your audit right away and will be in touch with our report in a few days! In the meantime, check out our churn
                reduction tool.
              </p>
              <div class="mt-12">
                <el-button @click="goToBuilder" type="primary" class="text-lg font-semibold py-4">Check it out →</el-button>
              </div>
            </div>
          </div>
        </wavy-card>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/api/auth.service';
import WavyCard from '@/components/UI/WavyCard.vue';

const stripeClientIdLive = 'ca_I4LOcF9sJYgxdwmpNGPMn0buaBAdyh6Q';

export default {
  name: 'OnboardConnectStripe',
  components: {
    WavyCard,
  },
  data() {
    return {
      stage: 'error', // connect, processing, error, ready
    };
  },
  async created() {
    if (this.$route.query.code) {
      const authCode = this.$route.query.code;
      await this.stripeAuthCallback(authCode);
    }
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    stripeRedirectUri() {
      // stripe requires https
      const origin = window.location.origin.replace('http:', 'https:');
      return window.encodeURIComponent(`${origin}${this.$route.path}`);
    },
    stripeConnectBaseUrl() {
      return `https://connect.stripe.com/oauth/authorize?response_type=code&scope=read_write&redirect_uri=${this.stripeRedirectUri}`;
    },
    stripeConnectUrlLive() {
      return `${this.stripeConnectBaseUrl}&client_id=${stripeClientIdLive}`;
    },
  },
  methods: {
    async stripeAuthCallback(authCode) {
      this.stage = 'processing';
      try {
        await AuthService.stripeAuthCallback(authCode, this.user._id);
        this.stage = 'ready';
        // remove audit flag once connected
        localStorage.removeItem('audit');
      } catch (error) {
        this.stage = 'error';
        console.log('error', error);
      }
    },
    goToBuilder() {
      this.$router.push('/cancellation/flows');
    },
    scheduleCall() {
      // eslint-disable-next-line no-undef
      SavvyCal('open', { link: 'churnkey-team/support', metadata: { page: window.location.pathname }, theme: 'os' });
    },
  },
};
</script>
<style>
.ck-button {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 0.375rem;
  box-shadow:
    0 0 transparent,
    0 0 transparent,
    var(--tw-shadow);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 transparent), var(--tw-ring-shadow, 0 0 transparent), var(--tw-shadow);
  box-shadow:
    0 0 0,
    0 4px 8px rgba(0, 0, 0, 0.15);
}

.ck-button.size-sm {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.ck-button.size-lg {
  font-weight: 600;
  padding: 1rem 4rem;
}

.ck-button:active {
  background-image: linear-gradient(rgba(0, 0, 0, 0.0314), rgba(0, 0, 0, 0.0314)) !important;
  box-shadow:
    0 0 1px rgba(0, 0, 0, 0.15),
    0 3px 6px rgba(0, 0, 0, 0.12);
  transform: scale(0.97);
  transition: transform 0.1s ease-out;
}
</style>
